import * as React from "react";

import { VizConfig } from "@/shared/config";
import { FileRecord } from "@/shared/domain/files";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";
import { SessionRecord } from "@/shared/domain/sessions";
import {
  TopicRecord,
  MessagePathRecord,
  MessagePathRepresentationMapping,
} from "@/shared/domain/topics";
import { State } from "@/shared/state/visualization";

import { MatchError } from "../layouts/LayoutToVizConfig";
import { Timer } from "../timer";

import { WorkspaceSource } from "./workspaceSources";

const noop = () => {};

export interface SetActiveLayoutOptions {
  skipReload?: boolean;
}

/**
 * The window of a file the session takes in, in nanoseconds since the Unix
 * epoch. The session's membership sets both bounds together or neither, so a
 * file the session takes in whole has no range at all rather than a half-open
 * one.
 */
export interface SessionClipRange {
  min: bigint;
  max: bigint;
}

/**
 * A session datasource the workspace could not read. `WorkspaceDataService`
 * returns the failure as a value rather than throwing, so one failed session
 * does not cost the user the rest of the workspace, and `reason` picks
 * which banner the workspace raises for it. `"unreadable"` covers every failure
 * other than a missing session or an unauthorized one.
 */
export interface SessionLoadFailure {
  sessionId: string;
  reason: "not-found" | "unauthorized" | "unreadable";
}

/**
 * A session loaded into the workspace: its record (name + `min_timestamp_ns`,
 * the sort key) and the live `FileRecord`s it contributed. Built by
 * `WorkspaceDataService` when it expands a session datasource, so it survives
 * save/share. A file shared by two sessions appears in each session's `files`.
 */
export interface Session {
  id: string;
  record: SessionRecord;
  files: FileRecord[];
  /**
   * The clip range for each member file whose membership declares one, keyed by
   * file id. A file with no entry contributes its whole time window. Read when
   * deriving the session's alignment source, so a clipped file narrows the
   * span its session spans rather than reporting the file's full extent.
   */
  clipRangeByFileId: Map<string, SessionClipRange>;
}

export interface EphemeralWorkspaceState {
  // Server state
  files: FileRecord[];
  /**
   * The sessions loaded into the workspace, pre-sorted by
   * `record.min_timestamp_ns` (earliest first, `null`s last). Empty when no
   * session datasource was loaded; the Files tab then falls back to its
   * type-bucketed layout. The Files tab derives Ungrouped files (in no session)
   * from the leftover of `files`.
   */
  sessions: Session[];
  /**
   * The session datasources that failed to load; the ones that loaded are in
   * `sessions`, and every session datasource lands in exactly one of the two.
   */
  sessionLoadFailures: SessionLoadFailure[];
  topics: TopicRecord[];
  messagePaths: MessagePathRecord[];
  /**
   * The workspace's alignment sources, derived by `deriveWorkspaceSources`
   * (`./workspaceSources`) from `files`, `topics`, and `sessions`. Derived once
   * by the provider, so every plot panel aligns series against the same
   * grouping and time bounds.
   */
  sources: WorkspaceSource[];
  /**
   * Each topic's message-path → representation mappings, computed once from
   * `topics`. Media panels use this to open their sources without a per-topic
   * representations request; keyed by topic id.
   */
  mappingsByTopicId: Map<string, MessagePathRepresentationMapping[]>;
  isDataLoading: boolean;
  error?: Error | null;
  layoutError?: Error | null;

  selectedPanelId: string | null;
  selectPanel: (panelId: string) => void;
  clearPanelSelection: () => void;
  /**
   * Clears the selection only when `panelId` is the one currently selected, so
   * that removing a panel the user has not selected leaves their selection
   * where it is.
   */
  clearPanelSelectionIfMatches: (panelId: string) => void;

  timer: Timer;

  // Hydration from a Layout or SharedWorkspace:
  isHydrating: boolean;
  setIsHydrating: (value: boolean) => void;

  // Active layout
  activeLayout: LayoutRecord;
  setActiveLayout: (
    layout: LayoutRecord,
    options?: SetActiveLayoutOptions,
  ) => void;
  skipLayoutRehydration: boolean;
  workspaceSnapshot: {
    panels: State["panels"];
    layout: State["layout"];
  };
  setWorkspaceSnapshot: (snapshot: {
    panels: State["panels"];
    layout: State["layout"];
  }) => void;

  // Keyed by panel id: the layout rules that bound to nothing in this
  // workspace when the layout was hydrated.
  dataMatchingErrors: Record<string, MatchError[]>;
  setDataMatchingErrors: (errors: Record<string, MatchError[]>) => void;

  // Ephemeral panel state (viewport, filters, etc.)
  ephemeralPanelState: Map<string, Record<string, unknown>>;
  updateEphemeralPanelState: (
    panelId: string,
    updates: Record<string, unknown>,
  ) => void;
}

/**
 * Workspace state with nothing loaded and every setter a no-op. Components
 * rendered outside a Workspace provider read this through the context below;
 * tests override individual fields on top of it to build fakes.
 *
 * Each call builds new objects because the timer, layout, and maps are
 * mutable; one shared instance would let one caller's edits show up for
 * another.
 */
export function createDefaultEphemeralWorkspaceState(): EphemeralWorkspaceState {
  return {
    files: [],
    sessions: [],
    sessionLoadFailures: [],
    topics: [],
    messagePaths: [],
    sources: [],
    mappingsByTopicId: new Map(),
    isDataLoading: false,
    error: null,
    layoutError: null,
    selectedPanelId: null,
    selectPanel: noop,
    clearPanelSelection: noop,
    clearPanelSelectionIfMatches: noop,
    timer: new Timer({ spans: [] }),
    // An unsaved placeholder carrying TEMPORARY_LAYOUT_ID, not a stored layout;
    // see that constant for how consumers present it.
    activeLayout: createEmptyLayout(),
    setActiveLayout: noop,
    skipLayoutRehydration: false,
    isHydrating: false,
    setIsHydrating: noop,
    dataMatchingErrors: {},
    setDataMatchingErrors: noop,
    workspaceSnapshot: {
      panels: VizConfig.default().panels,
      layout: VizConfig.default().layout,
    },
    setWorkspaceSnapshot: noop,
    ephemeralPanelState: new Map(),
    updateEphemeralPanelState: noop,
  };
}

export const EphemeralWorkspaceStateContext =
  React.createContext<EphemeralWorkspaceState>(
    createDefaultEphemeralWorkspaceState(),
  );
